import React, { useEffect, useState } from 'react'
import Header from '../../layouts/Header';
import { toast } from 'react-toastify';
import axios from '../../api/axios';
import { Button, Col, Form, Row, Spinner, Card, Table, Badge } from 'react-bootstrap';
import DatePicker from "react-datepicker";
import { format } from 'date-fns';

const OrderSummary = () => {
    const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
    const [skin, setSkin] = useState(currentSkin);

    const accessToken = localStorage.getItem('authToken');
    const [outlets, setOutlets] = useState([]);
    const [orderSummaries, setOrderSummaries] = useState([]);
    const [selectedOutlet, setSelectedOutlet] = useState('');
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [error, setError] = useState('');
    const [subBtnLoading, setSubBtnLoading] = useState(false);

    const getOutlets = async () => {
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
            };
            const response = await axios.get("outlet", { headers });
       
            if (response?.data?.status === true) {
                let filteredOutlets = response?.data?.data?.outlets?.filter((act) => act?.status === "ACTIVE");
                setOutlets(filteredOutlets);
            }
        } catch (error) {
            if (error.response && error.response?.data?.status === false) {
                let errorMessage = error.response.data.error;
                toast.error(errorMessage, {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored",
                });
            }
        }
    };
   
    const getOrderSummary = async (startDate, endDate, outletId) => {
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
            };
            let newUrl = `summary/day-order-summary?startDate=${startDate?.toISOString()?.split("T")[0]}&endDate=${endDate?.toISOString()?.split("T")[0]}`
            if (outletId) {
                newUrl += `&outletId=${outletId}`
            }
            const response = await axios.get(newUrl, { headers });
            console.log("Response of order summary :", response);
            
            setOrderSummaries(response?.data?.data?.data || [])
        } catch (error) {
            if (error.response && error.response?.data?.status === false) {
                let errorMessage = error.response.data.error;
                toast.error(errorMessage, {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored",
                });
            }
        }
    };

    const handleSubmit = async() => {
        if (!startDate || !endDate) {
            toast.error("Please select both start and end dates", {
                position: "top-right",
                autoClose: 3000,
                theme: "colored",
            });
            return;
        }
        
        if (startDate > endDate) {
            toast.error("Start date cannot be after end date", {
                position: "top-right",
                autoClose: 3000,
                theme: "colored",
            });
            return;
        }
        
        setSubBtnLoading(true);
        const adjustedStartDate = new Date(startDate);
        adjustedStartDate.setHours(0, 0, 0, 0);
        
        const adjustedEndDate = new Date(endDate);
        adjustedEndDate.setHours(23, 59, 59, 999);
        
        await getOrderSummary(adjustedStartDate, adjustedEndDate, selectedOutlet);
        setSubBtnLoading(false);
    }

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(value || 0);
    };

    // Calculate totals across all summaries
    const calculateTotals = () => {
        return orderSummaries.reduce((acc, summary) => {
            return {
                totalFoodAmount: acc.totalFoodAmount + (summary.totalFoodAmount || 0),
                totalBeverageAmount: acc.totalBeverageAmount + (summary.totalBeverageAmount || 0),
                totalTaxableAmount: acc.totalTaxableAmount + (summary.totalTaxableAmount || 0),
                totalDiscountAmount: acc.totalDiscountAmount + (summary.totalDiscountAmount || 0),
                totalTaxAmount: acc.totalTaxAmount + (summary.totalTaxAmount || 0),
                grandTotalAmount: acc.grandTotalAmount + (summary.grandTotalAmount || 0),
                totalInvoiceCount: acc.totalInvoiceCount + (summary.totalInvoiceCount || 0),
                totalCancelledOrderCount: acc.totalCancelledOrderCount + (summary.totalCancelledOrderCount || 0),
                cancelledOrderAmount: acc.cancelledOrderAmount + (summary.cancelledOrderAmount || 0),
                ordersWithCouponCount: acc.ordersWithCouponCount + (summary.ordersWithCouponCount || 0),
                totalCGST: acc.totalCGST + (summary.totalCGST || 0),
                totalSGST: acc.totalSGST + (summary.totalSGST || 0)
            };
        }, {
            totalFoodAmount: 0,
            totalBeverageAmount: 0,
            totalTaxableAmount: 0,
            totalDiscountAmount: 0,
            totalTaxAmount: 0,
            grandTotalAmount: 0,
            totalInvoiceCount: 0,
            totalCancelledOrderCount: 0,
            cancelledOrderAmount: 0,
            ordersWithCouponCount: 0,
            totalCGST: 0,
            totalSGST: 0
        });
    };

    // Get tax breakdown from summaries
    const getTaxBreakdown = () => {
        const taxRates = [
            { rate: 5, cgstField: 'cgst5PercentAmount', sgstField: 'sgst5PercentAmount' },
            { rate: 12, cgstField: 'cgst12PercentAmount', sgstField: 'sgst12PercentAmount' },
            { rate: 18, cgstField: 'cgst18PercentAmount', sgstField: 'sgst18PercentAmount' },
            { rate: 28, cgstField: 'cgst28PercentAmount', sgstField: 'sgst28PercentAmount' }
        ];

        return taxRates.map(tax => {
            const totalCGST = orderSummaries.reduce((sum, summary) => sum + (summary[tax.cgstField] || 0), 0);
            const totalSGST = orderSummaries.reduce((sum, summary) => sum + (summary[tax.sgstField] || 0), 0);
            
            if (totalCGST > 0 || totalSGST > 0) {
                const taxAmount = totalCGST + totalSGST;
                const taxable = taxAmount / (tax.rate / 100);
                
                return {
                    taxPercent: tax.rate,
                    taxable: taxable,
                    CGST: totalCGST,
                    SGST: totalSGST,
                    taxAmount: taxAmount,
                    grandTotal: taxable + taxAmount
                };
            }
            return null;
        }).filter(item => item !== null);
    };

    useEffect(() => {
        getOutlets();
    }, []);

    useEffect(() => {
        const now = new Date();
        const currentDate = new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate()
        );
        setStartDate(currentDate);
        setEndDate(currentDate);
    }, []);

    return (
        <React.Fragment>
            <Header onSkin={setSkin} />
            <div
                className={`main main-app p-3 p-lg-4 ${
                skin === "dark" ? "bg-dark text-light" : "bg-light"
                }`}
            >
                <div className="d-flex flex-column gap-3">
                    <div className={`card ${skin === "dark" ? "bg-dark-dark" : ""} border-0 shadow-sm`}>
                        <div className="card-header bg-white border-0 py-3">
                            <h4 className="mb-0 fw-bold">
                                <i className="bi bi-bag me-2"></i>ORDER SUMMARY
                            </h4>
                        </div>
                        <hr className="mt-0 mb-0" />
             
                        <div className="card-body">
                            {/* Filters Section */}
                            <div className="filters-section p-3 bg-light rounded-3 mb-4">
                                <Row className="g-3">
                                    <Col md={3}>
                                        <Form.Group controlId="outletSelect">
                                            <Form.Label className="fw-semibold">
                                                Select Outlet
                                            </Form.Label>
                                            <Form.Select
                                                value={selectedOutlet}
                                                onChange={(e) => setSelectedOutlet(e.target.value)}
                                                className="border-secondary"
                                            >
                                            <option value="">All Outlets</option>
                                            {outlets?.map((outName, index) => (
                                                <option key={index} value={outName?._id}>
                                                {outName?.name}
                                                </option>
                                            ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    <Col md={2}>
                                        <Form.Group controlId="startDatePicker">
                                            <Form.Label className="fw-semibold">
                                                Start Date <span className="text-danger">*</span>
                                            </Form.Label>
                                            <DatePicker
                                                selected={startDate}
                                                onChange={(date) => setStartDate(date)}
                                                dateFormat="dd-MM-yyyy"
                                                placeholderText="Select Start Date"
                                                className="form-control border-secondary"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={2}>
                                        <Form.Group controlId="endDatePicker">
                                            <Form.Label className="fw-semibold">
                                                End Date <span className="text-danger">*</span>
                                            </Form.Label>
                                            <DatePicker
                                                selected={endDate}
                                                onChange={(date) => setEndDate(date)}
                                                dateFormat="dd-MM-yyyy"
                                                placeholderText="Select End Date"
                                                className="form-control border-secondary"
                                                minDate={startDate}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={2} className="d-flex align-items-end">
                                        <Button
                                            variant="primary"
                                            className="w-100 py-2"
                                            onClick={handleSubmit}
                                            disabled={subBtnLoading || startDate === null || endDate === null}
                                        >
                                            {subBtnLoading ? (
                                                <Spinner size="sm" animation="border" />
                                                ) : (
                                                "Generate Report"
                                            )}
                                        </Button>
                                    </Col>
                                </Row>
                            </div>

                            {/* Report Section */}
                            {orderSummaries.length > 0 && (
                                <div className={`report-section ${skin === "dark" ? "text-light" : ""}`}>
                                    <Card className="mb-4 border-0 shadow-sm">
                                        <Card.Header className="bg-primary text-white">
                                            <h5 className="mb-0">
                                                Daily Sales Summary ({format(startDate, 'dd MMM yyyy')} - {format(endDate, 'dd MMM yyyy')})
                                            </h5>
                                        </Card.Header>
                                        <Card.Body>
                                            {/* Summary of all days */}
                                            <Row className="mb-3">
                                                <Col md={6}>
                                                    <div className="d-flex justify-content-between mb-2">
                                                        <span className="fw-semibold">Outlet:</span>
                                                        <span>{selectedOutlet ? outlets.find(o => o._id === selectedOutlet)?.name : 'All Outlets'}</span>
                                                    </div>
                                                    <div className="d-flex justify-content-between mb-2">
                                                        <span className="fw-semibold">Date Range:</span>
                                                        <span>{format(startDate, 'dd MMM yyyy')} to {format(endDate, 'dd MMM yyyy')}</span>
                                                    </div>
                                                    <div className="d-flex justify-content-between mb-2">
                                                        <span className="fw-semibold">Total Days:</span>
                                                        <span>{orderSummaries.length}</span>
                                                    </div>
                                                </Col>
                                                <Col md={6}>
                                                    <div className="d-flex justify-content-between mb-2">
                                                        <span className="fw-semibold">Total Orders:</span>
                                                        <span>{calculateTotals().totalInvoiceCount}</span>
                                                    </div>
                                                    <div className="d-flex justify-content-between mb-2">
                                                        <span className="fw-semibold">Cancelled Orders:</span>
                                                        <span>{calculateTotals().totalCancelledOrderCount} ({formatCurrency(calculateTotals().cancelledOrderAmount)})</span>
                                                    </div>
                                                    <div className="d-flex justify-content-between mb-2">
                                                        <span className="fw-semibold">Orders with Coupons:</span>
                                                        <span>{calculateTotals().ordersWithCouponCount}</span>
                                                    </div>
                                                </Col>
                                            </Row>

                                            <hr />

                                            <Row>
                                                <Col md={6}>
                                                    <h6 className="mb-3 fw-semibold">SALES BREAKDOWN (TOTAL)</h6>
                                                    <Table bordered className="mb-0">
                                                        <tbody>
                                                            <tr>
                                                                <td className="fw-semibold">Food Sales</td>
                                                                <td className="text-end">{formatCurrency(calculateTotals().totalFoodAmount)}</td>
                                                            </tr>
                                                            <tr>
                                                                <td className="fw-semibold">Beverage Sales</td>
                                                                <td className="text-end">{formatCurrency(calculateTotals().totalBeverageAmount)}</td>
                                                            </tr>
                                                            <tr>
                                                                <td className="fw-semibold">Total Sales</td>
                                                                <td className="text-end fw-bold">{formatCurrency(calculateTotals().totalFoodAmount + calculateTotals().totalBeverageAmount)}</td>
                                                            </tr>
                                                        </tbody>
                                                    </Table>
                                                </Col>
                                                <Col md={6}>
                                                    <h6 className="mb-3 fw-semibold">FINANCIAL SUMMARY (TOTAL)</h6>
                                                    <Table bordered className="mb-0">
                                                        <tbody>
                                                            <tr>
                                                                <td className="fw-semibold">Taxable Amount</td>
                                                                <td className="text-end">{formatCurrency(calculateTotals().totalTaxableAmount)}</td>
                                                            </tr>
                                                            <tr>
                                                                <td className="fw-semibold">Total Discount</td>
                                                                <td className="text-end text-danger">-{formatCurrency(calculateTotals().totalDiscountAmount)}</td>
                                                            </tr>
                                                            <tr>
                                                                <td className="fw-semibold">Total Tax</td>
                                                                <td className="text-end">{formatCurrency(calculateTotals().totalTaxAmount)}</td>
                                                            </tr>
                                                            <tr className="table-active">
                                                                <td className="fw-semibold">Grand Total</td>
                                                                <td className="text-end fw-bold text-success">{formatCurrency(calculateTotals().grandTotalAmount)}</td>
                                                            </tr>
                                                        </tbody>
                                                    </Table>
                                                </Col>
                                            </Row>

                                            <hr />

                                            {/* Daily breakdown */}
                                            <div className="mt-4">
                                                <h6 className="mb-3 fw-semibold">DAILY BREAKDOWN</h6>
                                                <Table bordered responsive>
                                                    <thead>
                                                        <tr>
                                                            <th>Date</th>
                                                            <th>Outlet</th>
                                                            <th>Invoices</th>
                                                            <th>Food Sales</th>
                                                            <th>Beverage Sales</th>
                                                            <th>Taxable</th>
                                                            <th>Discount</th>
                                                            <th>Tax</th>
                                                            <th>Grand Total</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {orderSummaries.map((summary, index) => (
                                                            <tr key={index}>
                                                                <td>{format(new Date(summary.startDate), 'dd MMM yyyy')}</td>
                                                                <td>{summary.outlet}</td>
                                                                <td>{summary.totalInvoiceCount}</td>
                                                                <td>{formatCurrency(summary.totalFoodAmount)}</td>
                                                                <td>{formatCurrency(summary.totalBeverageAmount)}</td>
                                                                <td>{formatCurrency(summary.totalTaxableAmount)}</td>
                                                                <td className="text-danger">-{formatCurrency(summary.totalDiscountAmount)}</td>
                                                                <td>{formatCurrency(summary.totalTaxAmount)}</td>
                                                                <td className="fw-bold text-success">{formatCurrency(summary.grandTotalAmount)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                    <tfoot>
                                                        <tr className="table-active">
                                                            <td colSpan="2" className="fw-bold">TOTAL</td>
                                                            <td className="fw-bold">{calculateTotals().totalInvoiceCount}</td>
                                                            <td className="fw-bold">{formatCurrency(calculateTotals().totalFoodAmount)}</td>
                                                            <td className="fw-bold">{formatCurrency(calculateTotals().totalBeverageAmount)}</td>
                                                            <td className="fw-bold">{formatCurrency(calculateTotals().totalTaxableAmount)}</td>
                                                            <td className="fw-bold text-danger">-{formatCurrency(calculateTotals().totalDiscountAmount)}</td>
                                                            <td className="fw-bold">{formatCurrency(calculateTotals().totalTaxAmount)}</td>
                                                            <td className="fw-bold text-success">{formatCurrency(calculateTotals().grandTotalAmount)}</td>
                                                        </tr>
                                                    </tfoot>
                                                </Table>
                                            </div>

                                            {/* Tax details */}
                                            <div className="mt-4">
                                                <h6 className="mb-3 fw-semibold">TAX DETAILS (AGGREGATED)</h6>
                                                <Table bordered responsive>
                                                    <thead>
                                                        <tr>
                                                            <th>Tax Rate</th>
                                                            <th>Taxable Amount</th>
                                                            <th>CGST</th>
                                                            <th>SGST</th>
                                                            <th>Total Tax</th>
                                                            <th>Grand Total</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {getTaxBreakdown().map((tax, index) => (
                                                            <tr key={index}>
                                                                <td>{tax.taxPercent}%</td>
                                                                <td>{formatCurrency(tax.taxable)}</td>
                                                                <td>{formatCurrency(tax.CGST)}</td>
                                                                <td>{formatCurrency(tax.SGST)}</td>
                                                                <td>{formatCurrency(tax.taxAmount)}</td>
                                                                <td>{formatCurrency(tax.grandTotal)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        </Card.Body>
                                        <Card.Footer className="text-muted">
                                            <small>Summary generated on {format(new Date(), 'dd MMMM yyyy hh:mm a')}</small>
                                        </Card.Footer>
                                    </Card>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    )
}

export default OrderSummary